import { Component, HostListener, OnInit, ViewEncapsulation, ViewChild, ElementRef, AfterViewInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';

import { Client } from 'src/providers/client/client';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Me } from 'src/providers/me/me';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { Card } from 'src/providers/card/card';
import { StripeProvider } from 'src/providers/stripe/stripe-provider';

import { Util } from 'src/shared/util/util';
import { ThemeService } from 'src/shared/services/theme.service';

import { TimeoutableComponent } from 'src/shared/components/timeoutable/timeoutable.component';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { SuccessPage } from 'src/shared/pages/success/success';

const BREAK_LARGE_RESOLUTION = 1105;

@Component({
    templateUrl: 'change-subscription.html',
    styleUrls: ['./change-subscription.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class ChangeSubscriptionPage extends TimeoutableComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('cardInfo', { static: false }) cardInfo: ElementRef;
  captchaErrorMessage: boolean;
  hasValidCaptcha: any;
  cardEl: any = null;
  cardHandler = this.onChange.bind(this);
  cost: any;
  currentSubscription: any;
  currentCostPerUnit: number;
  hasCustomSubscription: boolean;
  numberOfUnits: any;
  form: UntypedFormGroup;
  errorMessage: string;
  isMobileResolution = window.innerWidth <= BREAK_LARGE_RESOLUTION;
  selectedSubscription: any;
  selectedCost: number;
  selectedCostPerUnit: number;
  subscriptionItems: any;
  subscriptionTypes: any;
  isRightSideContent = true;
  dialogParams: any;
  flexSubscription: any;
  standardYearlySubscription: any;
  standardMonthlySubscription: any;
  isAccountSuspended: boolean;
  hasNegativeBalance: boolean;
  howToPayItems: any;
  howToPayLabel: string;
  showPaymentMethodOptions = true;
  saveCardForFuture = true;
  newCard: any;
  submitted = false;
  addresses: any;
  numberOfPaidProperties: any;
  @HostListener("window:resize", ["$event"])
  onResize(event) {
    this.isMobileResolution = event.target.innerWidth <= BREAK_LARGE_RESOLUTION;
  }
  boundSave = this.save.bind(this);

constructor(
    private client: Client,
    private fb: UntypedFormBuilder,
    private me: Me,
    private navCtrl: CustomNavController,
    private storage: TidyStorage,
    private util: Util,
    private rightSidePanelService: RightSidePanelService,
    public windowService: WindowService,
    private card: Card,
    private stripeProvider: StripeProvider,
    private themeService: ThemeService,
    private cd: ChangeDetectorRef
  ) {
    super();
    this.form = this.fb.group({
      subscription: ['', Validators.required],
      yearlyMonthly: [''],
      howToPay: ['', Validators.required],
    });

    // Listen for changes to howToPay to mount Stripe when needed
    this.form.controls.howToPay.valueChanges.subscribe(value => {
      if (value === 'newCard') {
        setTimeout(() => this.mountStripeForm(), 100);
      }
    });
  }

  async ngOnInit() {
    try {
      this.loaded = false;
      this.rightSidePanelService.setDialogLoading(true);
      const [addresses, hasNegativeBalance, me, subscriptionTypes] = await Promise.all([
        this.client.getAddresses(),
        this.me.getHasNegativeBalance(),
        this.me.load(true),
        this.me.getSubscriptionTypes()
      ]);
      this.addresses = addresses;
      this.hasNegativeBalance = hasNegativeBalance;
      this.isAccountSuspended = me.suspended_at ? true : false;
      this.subscriptionTypes = subscriptionTypes;
      this.rightSidePanelService.setDialogPageTitle(this.hasNegativeBalance ? 'Subscription Payment Issue' : 'Change Subscription');
      this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
      if (this.isRightSideContent) {
        this.dialogParams = await this.storage.retrieve('dialog-params');
      }
      const currentSubscription = await this.getSubscription();
      this.hasCustomSubscription = this.checkIfHasCustomSubscription(currentSubscription);
      if (!this.hasCustomSubscription) {
        this.flexSubscription = subscriptionTypes.find((type) => type.key === 'flex');
        this.standardYearlySubscription = subscriptionTypes.find((type) => type.key === 'standard_yearly');
        this.standardMonthlySubscription = subscriptionTypes.find((type) => type.key === 'standard_monthly');
        this.currentSubscription = subscriptionTypes.find((type) => type.key === currentSubscription?.plan.key);
        this.selectedSubscription = this.currentSubscription;
        this.form.patchValue({ yearlyMonthly: (this.currentSubscription?.key === 'standard_yearly' || this.currentSubscription?.key === 'standard_monthly') && this.currentSubscription?.renewal_frequency === 'monthly' ? 'monthly' : 'yearly' });
      }
      if (this.hasNegativeBalance || this.isAccountSuspended) {
        const cards = await this.card.getCreditCards();
        this.howToPayItems = this.buildHowToPayItems(cards);
        const hasCurrentCard = this.howToPayItems.some(item => item.value === 'currentCard');
        if (hasCurrentCard) {
          this.form.patchValue({ howToPay: 'currentCard' });
        } else {
          this.form.patchValue({ howToPay: 'newCard' });
        }
      }
      this.rightSidePanelService.setDialogLoading(false);
      this.loaded = true;
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  checkIfHasCustomSubscription(currentSubscription: any) {
    if (currentSubscription?.plan?.key !== 'standard_yearly' && currentSubscription?.plan?.key !== 'standard_monthly' && currentSubscription?.plan?.key !== 'flex') {
      this.selectedSubscription = currentSubscription;
      return true;
    } else {
      return false;
    }
  }

  ngAfterViewInit() {
    // Mount Stripe form after view is initialized if 'newCard' is selected
    if (this.loaded && this.form.value.howToPay === 'newCard') {
      setTimeout(() => this.mountStripeForm(), 0);
    }
  }

  mountStripeForm() {
    this.cardEl = this.stripeProvider.getCardElement(!this.themeService.isDarkMode());
    if (this.cardInfo) {
      this.cardEl.mount(this.cardInfo.nativeElement);
      this.cardEl.addEventListener('change', this.cardHandler);
    }
  }

  ngOnDestroy() {
    if (this.cardEl && this.cardInfo) {
      this.cardEl.unmount(this.cardInfo.nativeElement);
      this.cardEl.removeEventListener('change', this.cardHandler);
    }
    this.stripeProvider.destroyCard();
  }

  onChange(err: any) {
    this.errorMessage = err.error ? err.error.message : err.message;
    this.cd.detectChanges();
  }

  async getSubscription(): Promise<any> {
    let nextSubscription = await this.getParam('nextSubscription');
    if (nextSubscription) {
      return nextSubscription;
    }
    let currentSubscription = await this.getParam('currentSubscription');
    if (currentSubscription) {
      return currentSubscription;
    }
    const subscriptions = await this.me.getSubscriptions();
    if (subscriptions.length > 1) {
      subscriptions.map((subscription) => {
        if (subscription.is_activated_in_next_cycle) {
          nextSubscription = subscription;
        } else {
          currentSubscription = subscription;
        }
      });
    } else {
      currentSubscription = subscriptions[0];
    }
    if (nextSubscription) {
      return nextSubscription;
    }
    if (currentSubscription) {
      return currentSubscription;
    }
  }


  async getParam(paramName): Promise<any> {
    let param = this.dialogParams?.[paramName];
    if (param === null || param === undefined) {
      param = this.navCtrl.getParam(paramName)
    }
    if (param === null || param === undefined) {
      param = await this.storage.retrieve(paramName);
    }
    this.storage.save(paramName, param);
    return param;
  }

  selectSubscription(type) {
    this.selectedSubscription = {};
    if (type === 'autopilot') {
      return this.selectedSubscription['key'] = 'autopilot';
    }
    if (type === 'standard') {
      this.selectStandardSubscription();
    } else {
      this.selectedSubscription = this.subscriptionTypes.find((t) => t.key === type);
    }
  }

  selectStandardSubscription() {
    if (this.form.value.yearlyMonthly === 'yearly') {
      this.selectedSubscription = this.subscriptionTypes.find((type) => type.key === 'standard_yearly');
    } else {
      this.selectedSubscription = this.subscriptionTypes.find((type) => type.key === 'standard_monthly');
    }
    this.numberOfPaidProperties = this.addresses.length - this.selectedSubscription?.debit_strategies?.debit_by_address?.number_of_free_addresses;
  }

  async save() {
    try {
      if (this.hasNegativeBalance) {
        const billingData = await this.client.getBillingData();
        if (this.form.value.howToPay === 'newCard') {
          this.captchaErrorMessage = false;
          if (this.hasValidCaptcha === null) {
            return this.captchaErrorMessage = true;
          }
          const cardData: any = await this.getStripeCardData();
          await this.card.saveCard(cardData);
        } else {
          const chargePayload: any = {
            amount: billingData.balance*-1
          };
          if (this.form.value.howToPay === 'currentCard') {
            chargePayload.payment_method_id = this.howToPayItems.find(item => item.value === 'currentCard')?.cardId;
          } else {
            chargePayload.payment_method_id = this.form.value.howToPay;
          }
          await this.card.chargeCard(chargePayload);
        }
      }
      const payload = {
        plan_type_key: this.selectedSubscription?.key
      }
      await this.me.changeSubscription(payload);
      this.util.showSuccess('Subscription updated successfully.');
      if (this.hasNegativeBalance) {
        window.location.reload();
      } else {
        this.rightSidePanelService.navigateTo('subscription');
      }
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  mountSuccessPageParams() {
    let header = 'Subscription Changed';
    if (this.hasNegativeBalance) {
      header = 'Payment & Subscription Updated';
    }
    return {
      header,
      body: '',
      buttonText: 'Ok',
      buttonRoute: 'more'
    };
  }

  goToLearnMoreAdvanced() {
    this.util.openUrl('https://www.tidy.com/demo');
  }

  goToLearnMorePlans() {
    this.util.openUrl('https://www.tidy.com/pricing');
  }

  buildHowToPayItems(cards: any[]) {
    const options = [];
    const lastFourCC = this.getLastFourFromCards(cards);
    if (lastFourCC) {
      options.push({
        viewValue: `Primary credit card on file (ends in ${lastFourCC})`,
        value: 'currentCard',
        cardId: cards.find(card => card.is_primary)?.id
      });
    }
    cards.forEach((card: any) => {
      if (!card.is_primary && card.metadata?.last4) {
        options.push({
          viewValue: `Card ending in ${card.metadata.last4}`,
          value: card.id
        });
      }
    });
    options.push({
      viewValue: 'Add New Card',
      value: 'newCard'
    })
    return options;
  }

  getLastFourFromCards(cards: any[]): string {
    let lastFourCC = null;
    if (cards && cards.length > 0) {
      cards.map((card: any) => {
        if (card.is_primary) {
          lastFourCC = card.metadata?.last4;
        }
      });
    }
    return lastFourCC;
  }

  async getStripeCardData() {
    const stripeData = await this.stripeProvider.getInstance().createToken(this.cardEl);
    if (stripeData?.error) {
      throw stripeData.error;
    }
    return {
      type: 'card',
      stripe_card_token: stripeData.token.id,
      primary_payment_method: true,
    };
  }

  captchaEvents(captchaResult) {
    this.hasValidCaptcha = captchaResult;
  }

}
